import React, { useEffect, useMemo } from "react";

import {
    asString,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { createTextValidator } from "../validation/defaultValidators";

export interface Option {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SelectFieldProps
    extends Omit<
        React.SelectHTMLAttributes<HTMLSelectElement>,
        "name" | "defaultValue"
    > {
    label: string;
    name: string;
    options: Option[];
    defaultValue?: string;
    validate?: (value: string) => boolean;
    validationMessage?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
    label,
    name,
    options,
    defaultValue = "",
    className,
    required = false,
    validate,
    validationMessage,
    ...selectProps
}) => {
    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createTextValidator(required),
        [required, validate]
    );

    useEffect(() => {
        if (!registerFieldValidation) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asString(value)),
        });
    }, [effectiveValidator, name, registerFieldValidation, validationMessage]);

    const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (event) => {
        clearFieldError(name);
        selectProps.onChange?.(event);
    };

    return (
        <div className="form-field">
            <label htmlFor={name}>{label}</label>

            <span className="wpcf7-form-control-wrap" data-name={name}>
                <select
                    {...selectProps}
                    id={name}
                    name={name}
                    required={required}
                    onChange={handleChange}
                    className={[
                        "input",
                        "input--select",
                        fieldError ? "wpcf7-not-valid" : null,
                        className,
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    defaultValue={defaultValue}
                >
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                {fieldError ? (
                    <span className="wpcf7-not-valid-tip" aria-hidden="true">
                        {fieldError}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

